import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "@/shared/lib/utils"

const badgeVariants = cva(
  // Sora-spec badge: pill shape, opacity-driven hierarchy, single-pixel focus
  // border (no 3px halo). No shadow transitions.
  "inline-flex items-center justify-center rounded-full border border-transparent px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-1 focus-visible:outline-ring aria-invalid:outline-destructive transition-colors overflow-hidden",
  {
    variants: {
      variant: {
        // Default: 6% white wash + 8% hairline — Sora opacity hierarchy.
        default: "bg-foreground/[0.06] text-foreground border-foreground/[0.08] [a&]:hover:bg-foreground/[0.10]",
        secondary:
          "bg-foreground/[0.10] text-foreground [a&]:hover:bg-foreground/[0.14]",
        destructive:
          "bg-destructive text-white [a&]:hover:bg-destructive/90",
        outline:
          "border-border text-foreground [a&]:hover:bg-foreground/[0.04]",
        ghost: "[a&]:hover:bg-foreground/[0.04] [a&]:hover:text-foreground",
        link: "text-foreground underline-offset-4 [a&]:hover:underline",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function Badge({
  className,
  variant = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : "span"

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  )
}

export { Badge, badgeVariants }
